// src/lib/config-completude.js — « CE QUI MANQUE » : audit de complétude du blob cfg.
//
// ┌──────────────────────────────────────────────────────────────────────────┐
// │ MODULE PUR — zéro I/O, zéro réseau, zéro import Supabase. Entrée = `cfg` │
// │ (le blob fbz8-cfg / cfg_config.data), sortie = une liste de manques.     │
// └──────────────────────────────────────────────────────────────────────────┘
//
// À QUOI ÇA SERT : répondre à UNE question — « qu'est-ce qui n'est pas rempli,
// et qu'est-ce que ça bloque ? ». Un champ vide dans ce produit ne fait pas
// toujours du bruit : la moitié des manques ci-dessous éteignent une
// fonctionnalité EN SILENCE (un email qui ne part pas, une garde qui se
// désarme). C'est précisément ceux-là qu'un admin ne peut pas trouver seul.
//
// 🔴 RÈGLE DE CE FICHIER — UNE RÈGLE = UNE LECTURE DU CODE QUI LA JUSTIFIE.
// Chaque entrée de REGLES porte, en commentaire, le fichier + la ligne qui
// prouve ce qui est bloqué. Aucune règle « par prudence » : si aucun
// consommateur ne casse quand le champ est vide, il n'y a PAS de manque.
// Toute règle ajoutée ici doit citer sa source de la même façon.
//
// CE QU'IL NE FAIT PAS : il ne corrige rien, ne sauvegarde rien, ne connaît ni
// la base ni le réseau. La vue qui l'affiche vit dans src/tabs/CompletudePanel.jsx.

import { getAllLogsFlat } from './sousloc-helpers'
// Miroirs CLIENT des résolveurs serveur (chacun documente sa source et son test
// de parité) — on les RÉUTILISE au lieu de redire la règle une 3e fois :
//   hasValidBareme  ← api/sites.js::resolveBaremeTaxe   (Q1 STRICT)
//   hasCautionAmount ← api/sites.js::resolveCautionFromLog (D3)
import { hasValidBareme } from './taxe-commune-helpers'
import { hasCautionAmount } from './caution-helpers'
import { propDisplayName } from './format'

// ── Gravités ──────────────────────────────────────────────────────────────
// BLOQUANT    : quelque chose NE PEUT PAS se produire du tout (un chemin
//               fail-closed renvoie une erreur, l'action est refusée).
// SILENCIEUX  : ça marche, mais quelque chose disparaît sans le dire (un envoi
//               sauté, une garde neutralisée, un bloc masqué) — le pire cas
//               pour un admin, parce que rien ne le signale.
// INFORMATIF  : cosmétique ou optionnel, aucune conséquence fonctionnelle.
export const GRAVITE = Object.freeze({
  BLOQUANT: 'bloquant',
  SILENCIEUX: 'silencieux',
  INFORMATIF: 'informatif',
})

// Ordre de tri : le bloquant d'abord, l'informatif en dernier.
const ORDRE_GRAVITE = { [GRAVITE.BLOQUANT]: 0, [GRAVITE.SILENCIEUX]: 1, [GRAVITE.INFORMATIF]: 2 }

// Portées — « OÙ » se situe le manque.
export const PORTEE = Object.freeze({
  SOCIETE: 'societe',
  PROPRIETAIRE: 'proprietaire',
  LOGEMENT: 'logement',
})

// MIROIR de lib/emitter-resolver.js (`SOUS_LOC_PROP_ID = 'p6'`) : le
// propriétaire p6 « Maison Zen » N'EST PAS un bailleur personne physique, c'est
// FULLBNBZEN SAS en sous-location. Ses coordonnées viennent de `cfg.co`, jamais
// de la fiche propriétaire — d'où son exclusion des règles « bailleur ».
const SOUS_LOC_PROP_ID = 'p6'

/** Vide = null, undefined, ou une chaîne qui ne contient que des blancs. */
function estVide(v) {
  return v == null || String(v).trim() === ''
}

/** Libellé lisible d'un propriétaire, sans jamais rendre une chaîne vide. */
function nomProprio(prop) {
  return propDisplayName(prop) || prop?.nom || `Propriétaire ${prop?.id || '?'}`
}

/** Libellé lisible d'un logement. */
function nomLogement(log) {
  return log?.nom_complet || log?.nom || log?.id || 'Logement sans nom'
}

// ── Emplacements de saisie (« OÙ le remplir ») ────────────────────────────
// Un manque sans adresse de réparation est un reproche, pas une aide. Chaque
// emplacement est vérifié dans le code de l'écran correspondant.
const OU = {
  // src/tabs/ParamsPanel.jsx — carte « Informations société », sous-onglet Général.
  SOCIETE_GENERAL: { onglet: 'Paramètres', carte: 'Général › Informations société' },
  // src/tabs/logements/InfoFbzTab.jsx:844 carte « Administration », sous-section
  // « Propriétaire » (adresse/CP/ville, l. 992-1014).
  ADMIN_PROPRIO: { onglet: 'Logements', carte: 'Info FBZ › Administration › Propriétaire' },
  // src/tabs/LogementsPanel.jsx — édition inline du nom dans la colonne de gauche.
  LISTE_PROPRIO: { onglet: 'Logements', carte: 'Colonne de gauche › nom du propriétaire' },
  // src/tabs/logements/InfoFbzTab.jsx:1189 sous-section « Stripe propriétaire ».
  ADMIN_STRIPE: { onglet: 'Logements', carte: 'Info FBZ › Administration › Stripe propriétaire' },
  // src/tabs/logements/InfoFbzTab.jsx:315 carte « Tarification générale »
  // (TaxeReminderPanel l. 398 + TaxeBaremeDetector).
  TARIFICATION: { onglet: 'Logements', carte: 'Info FBZ › Tarification générale' },
  // src/tabs/logements/LivretTab.jsx:429 — carte « WiFi + Livret externe ».
  LIVRET_INSTRUCTIONS: { onglet: 'Logements', carte: 'Livret › Instructions › WiFi + Livret externe' },
  // src/tabs/logements/LivretTab.jsx — carte « Caractéristiques » (capacité).
  LIVRET_CARACTERISTIQUES: { onglet: 'Logements', carte: 'Livret › Instructions › Caractéristiques' },
  // src/tabs/logements/InfoFbzTab.jsx:426 carte « Réservations en direct ».
  DIRECT: { onglet: 'Logements', carte: 'Info FBZ › Réservations en direct' },
}

/**
 * Construit un manque. `id` doit être STABLE (clé de rendu React + tests).
 */
function manque({ id, gravite, portee, cible, champ, libelle, bloque, ou, note }) {
  return { id, gravite, portee, cible, champ, libelle, bloque, ou, note: note || null }
}

// ═══════════════════════════════════════════════════════════════════════════
// RÈGLES — SOCIÉTÉ (cfg.co)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * MÉDIATEUR DE LA CONSOMMATION — art. 16 du contrat.
 *
 * JUSTIFICATION (lue, pas supposée) :
 *   - lib/contrat-html.js:450-451 — `cfg.co.mediateur_nom` / `mediateur_coordonnees`
 *     (repli `process.env.MEDIATEUR_NOM` / `MEDIATEUR_COORDONNEES`).
 *   - lib/contrat-html.js:284 — le paragraphe est gardé par
 *     `{#if loueur_professionnel ET mediateur_nom ET mediateur_coordonnees}` :
 *     les deux champs vides ⇒ l'article DISPARAÎT, sans erreur ni trace.
 *   - lib/contrat-vars.js:414 — `loueur_professionnel: em.isSousLoc ? '1' : ''`
 *     ⇒ l'article n'existe QUE lorsque l'émetteur est FULLBNBZEN SAS (p6).
 *
 * ⇒ SILENCIEUX, et signalé UNIQUEMENT si un propriétaire sous-location existe.
 * Chez un propriétaire particulier, l'absence de médiateur est NORMALE (il n'est
 * pas professionnel au sens de L. 612-1 du Code de la consommation) : le
 * signaler serait un faux positif permanent.
 *
 * ⚠️ Le repli `process.env` n'est pas lisible depuis le client : si les
 * variables Vercel sont posées, ce manque est un faux positif. C'est dit dans
 * `note` plutôt que deviné.
 */
function reglesSociete(cfg) {
  const out = []
  const co = cfg?.co || {}
  // `Array.isArray` et pas `|| []` : un blob corrompu peut porter n'importe quoi
  // dans `props` (une chaîne, un objet) — un écran d'audit ne doit jamais
  // planter sur la donnée qu'il est censé diagnostiquer.
  const listeProps = Array.isArray(cfg?.props) ? cfg.props : []
  const aSousLocation = listeProps.some((p) => p?.id === SOUS_LOC_PROP_ID)
  if (!aSousLocation) return out

  const nomVide = estVide(co.mediateur_nom)
  const coordVide = estVide(co.mediateur_coordonnees)
  if (nomVide || coordVide) {
    out.push(
      manque({
        id: 'societe.mediateur',
        gravite: GRAVITE.SILENCIEUX,
        portee: PORTEE.SOCIETE,
        cible: { label: 'FULLBNBZEN SAS' },
        champ:
          nomVide && coordVide
            ? 'cfg.co.mediateur_nom + cfg.co.mediateur_coordonnees'
            : nomVide
              ? 'cfg.co.mediateur_nom'
              : 'cfg.co.mediateur_coordonnees',
        libelle: 'Médiateur de la consommation',
        bloque:
          "L'article 16 du contrat (médiateur de la consommation) disparaît du document, " +
          'sans erreur ni avertissement. Il ne concerne que les contrats émis par ' +
          'FULLBNBZEN SAS en sous-location (chambres de la Maison Zen).',
        ou: OU.SOCIETE_GENERAL,
        note:
          'Un repli par variables d’environnement Vercel (MEDIATEUR_NOM / ' +
          'MEDIATEUR_COORDONNEES) existe côté serveur : s’il est renseigné, ' +
          'l’article s’affiche quand même. Cet écran ne peut pas le voir.',
      }),
    )
  }
  return out
}

// ═══════════════════════════════════════════════════════════════════════════
// RÈGLES — PROPRIÉTAIRE (identité du bailleur sur le contrat)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * IDENTITÉ DU LOUEUR — nom + adresse + (CP ∧ ville).
 *
 * JUSTIFICATION :
 *   - lib/contrat-loueur-identite.js:28-31 — CHAMPS_REQUIS = nom, adresse ;
 *     l. 54-57 — CP et ville exigés ENSEMBLE dès que l'adresse est là (« une rue
 *     sans commune n'identifie personne »), un seul message plutôt que trois.
 *   - lib/contrat-loueur-identite.js:51 — `if (emitter.isSousLoc) return ok`
 *     ⇒ la sous-location FBZ (p6) est EXCLUE par construction.
 *   - api/sites.js handleContractRead — verdict faux ⇒ 503 `contract_unavailable`
 *     (le voyageur voit « contrat momentanément indisponible »).
 *   - api/sites.js handleContractSign + api/contract-pdf.js — même garde
 *     fail-closed posée par ce lot : ni signature ni archivage PDF.
 * ⇒ BLOQUANT : aucun contrat ne peut être produit pour AUCUN logement de ce
 *   propriétaire.
 *
 * PÉRIMÈTRE : seuls les propriétaires qui portent au moins un logement sont
 * évalués — api/sites.js::_resolveContractContext (l. 9105-9113) résout le
 * propriétaire À PARTIR du logement (`log.propId`). Sans logement, il n'existe
 * aucun chemin de contrat, donc rien n'est bloqué.
 */
function reglesProprietaire(cfg) {
  const out = []
  const props = Array.isArray(cfg?.props) ? cfg.props : []

  for (const prop of props) {
    if (!prop || typeof prop !== 'object') continue
    if (prop.id === SOUS_LOC_PROP_ID) continue // sous-location FBZ : cfg.co fait foi
    const nbLogements = Array.isArray(prop.logs) ? prop.logs.length : 0
    if (nbLogements === 0) continue // aucun contrat possible ⇒ rien de bloqué

    const cible = { propId: prop.id, propNom: nomProprio(prop) }
    const bloqueContrat =
      `Aucun contrat de location ne peut être généré, signé ni archivé pour ` +
      `les ${nbLogements} logement(s) de ce propriétaire : le voyageur reçoit ` +
      `« contrat momentanément indisponible ».`

    // NOM — `emitter.nom` vaut propDisplayName(prop) || prop.nom
    // (lib/emitter-resolver.js:20-27) : les trois champs vides ⇒ nom vide.
    if (estVide(propDisplayName(prop)) && estVide(prop.nom)) {
      out.push(
        manque({
          id: `proprio.nom:${prop.id}`,
          gravite: GRAVITE.BLOQUANT,
          portee: PORTEE.PROPRIETAIRE,
          cible,
          champ: 'props[].nom_famille / prenom (ou props[].nom)',
          libelle: 'Nom du propriétaire',
          bloque: bloqueContrat,
          ou: OU.LISTE_PROPRIO,
        }),
      )
    }

    // ADRESSE — puis CP ∧ ville, exigés ensemble et SEULEMENT si l'adresse
    // est renseignée (miroir exact de la règle serveur : un seul message).
    if (estVide(prop.adresse)) {
      out.push(
        manque({
          id: `proprio.adresse:${prop.id}`,
          gravite: GRAVITE.BLOQUANT,
          portee: PORTEE.PROPRIETAIRE,
          cible,
          champ: 'props[].adresse',
          libelle: 'Adresse du bailleur',
          bloque: bloqueContrat,
          ou: OU.ADMIN_PROPRIO,
          note: "Mention obligatoire d'un contrat de location.",
        }),
      )
    } else if (estVide(prop.code_postal) || estVide(prop.ville)) {
      out.push(
        manque({
          id: `proprio.cp-ville:${prop.id}`,
          gravite: GRAVITE.BLOQUANT,
          portee: PORTEE.PROPRIETAIRE,
          cible,
          champ: 'props[].code_postal / props[].ville',
          libelle: 'Code postal et ville du bailleur',
          bloque: bloqueContrat,
          ou: OU.ADMIN_PROPRIO,
          note: "Une rue sans commune n'identifie personne : les deux sont exigés avec l'adresse.",
        }),
      )
    }
  }
  return out
}

// ═══════════════════════════════════════════════════════════════════════════
// RÈGLES — LOGEMENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Les règles logement s'appliquent aux logements FEUILLES (getAllLogsFlat :
 * chambres de sous-location exposées, parent de regroupement exclu) — exactement
 * le périmètre que le serveur voit via lib/cfg-reader-server.js::flattenLogs.
 */
function reglesLogement(cfg) {
  const out = []
  const logs = getAllLogsFlat(cfg)

  for (const log of logs) {
    const cible = { logId: log.id, logNom: nomLogement(log), propId: log.propId, propNom: log.propNom }

    // ── TAXE DE SÉJOUR — barème validé ────────────────────────────────────
    // api/sites.js:2883 (`booking_quote` → 200 ok:false `bareme_non_valide`)
    // et api/sites.js:4580 (`booking create` → 400 `bareme_non_valide`) : sans
    // taux ET plafond finis > 0, AUCUN devis et AUCUNE demande. La PRÉSENCE du
    // barème vaut validation (api/sites.js:282-284 : posé uniquement par un
    // acte admin). Miroir client = hasValidBareme (taxe-commune-helpers.js:56).
    // ⚠️ HORS SCOPE de ce lot côté SAISIE : le barème se pose dans la fiche
    // logement (lot taxe de séjour dédié). On le SIGNALE, on n'ajoute rien.
    if (!hasValidBareme(log, cfg)) {
      out.push(
        manque({
          id: `log.taxe_sejour:${log.id}`,
          gravite: GRAVITE.BLOQUANT,
          portee: PORTEE.LOGEMENT,
          cible,
          champ: 'logs[].taxe_sejour (taux_pct + plafond_eur)',
          libelle: 'Barème de taxe de séjour',
          bloque:
            'Aucune réservation en direct n’est possible sur ce logement : le site ' +
            'refuse de calculer un devis et de déposer une demande (« réservation ' +
            'bientôt disponible »).',
          ou: OU.TARIFICATION,
        }),
      )
    }

    // ── CAUTION — montant défini ──────────────────────────────────────────
    // api/sites.js:2897 (devis → `caution_non_definie`) et api/sites.js:4593
    // (dépôt de demande → 400 `caution_non_definie`), juste APRÈS le gate
    // barème. Résolution self → parentLog (api/sites.js::resolveCautionFromLog,
    // l. 391-397) : une chambre de sous-location hérite du montant de la Maison
    // Zen. Miroir client = hasCautionAmount (caution-helpers.js:28).
    if (!hasCautionAmount(log)) {
      out.push(
        manque({
          id: `log.caution_amount:${log.id}`,
          gravite: GRAVITE.BLOQUANT,
          portee: PORTEE.LOGEMENT,
          cible,
          champ: 'logs[].caution_amount',
          libelle: 'Montant de la caution',
          bloque:
            'Aucune réservation en direct n’est possible sur ce logement : le devis ' +
            'et le dépôt de demande sont refusés, exactement comme sans barème de taxe.',
          ou: OU.ADMIN_STRIPE,
        }),
      )
    }

    // ── STRIPE — sous-compte du logement ──────────────────────────────────
    // Le manque ne bloque AUCUNE réservation (aucun gate `stripe_account_id`
    // sur le chemin de création, api/sites.js §7bis/7ter) — il éteint la
    // caution, en silence, à trois endroits :
    //   · lib/messages-engine.js:2488-2501 — le message de caution est SAUTÉ
    //     (`skipped_stripe_account_missing`), le voyageur ne reçoit rien ;
    //   · api/sites.js::_resolveOtaCautionUrl (l. 7533-7536) — pas de lien
    //     dans l'espace voyageur OTA, la page affiche « bientôt » ;
    //   · lib/livret-sections-handlers.js:1557-1566 — `caution_unavailable`
    //     ⇒ la condition caution du livret est NEUTRALISÉE : les instructions
    //     d'arrivée se débloquent SANS caution encaissée.
    // Résolution self → parentLog (lib/cfg-reader-server.js:117-126).
    // ⇒ SILENCIEUX : tout continue de fonctionner, la caution disparaît.
    const stripeAcct = log.stripe_account_id || log.parentLog?.stripe_account_id || null
    if (estVide(stripeAcct)) {
      out.push(
        manque({
          id: `log.stripe_account_id:${log.id}`,
          gravite: GRAVITE.SILENCIEUX,
          portee: PORTEE.LOGEMENT,
          cible,
          champ: 'logs[].stripe_account_id',
          libelle: 'Sous-compte Stripe du logement',
          bloque:
            'La caution cesse d’être demandée sans le dire : le message de caution ' +
            'est sauté, l’espace voyageur n’affiche aucun lien, et les instructions ' +
            'd’arrivée du livret se débloquent alors qu’aucune caution n’a été prise.',
          ou: OU.ADMIN_STRIPE,
        }),
      )
    }

    // ── LIEN AVIS GOOGLE ──────────────────────────────────────────────────
    // api/sites.js:4163-4167 — 3e verrou du gate avis : « sans lien renseigné,
    // AUCUNE invitation ne part » (règle explicite Dimitri). SELF-ONLY,
    // volontairement sans repli parent (l. 4170-4172 : une chambre n'hérite pas
    // du lien de la Maison Zen). 4e garde de SCHÉMA l. 4232 :
    // `if (!/^https?:\/\//i.test(raw)) return null` — une valeur non http(s)
    // vaut absence, en pire (elle a l'air remplie).
    // ⇒ SILENCIEUX : la demande d'avis ne part jamais, sans erreur.
    const avisBrut = typeof log.avis_google_url === 'string' ? log.avis_google_url.trim() : ''
    if (avisBrut === '') {
      out.push(
        manque({
          id: `log.avis_google_url:${log.id}`,
          gravite: GRAVITE.SILENCIEUX,
          portee: PORTEE.LOGEMENT,
          cible,
          champ: 'logs[].avis_google_url',
          libelle: 'Lien avis Google',
          bloque:
            'L’email de demande d’avis ne part jamais pour ce logement, et l’espace ' +
            'voyageur n’affiche aucune invitation. Rien ne le signale.',
          ou: OU.LIVRET_INSTRUCTIONS,
        }),
      )
    } else if (!/^https?:\/\//i.test(avisBrut)) {
      out.push(
        manque({
          id: `log.avis_google_url_schema:${log.id}`,
          gravite: GRAVITE.SILENCIEUX,
          portee: PORTEE.LOGEMENT,
          cible,
          champ: 'logs[].avis_google_url',
          libelle: 'Lien avis Google au format inattendu',
          bloque:
            'Le lien est rempli mais ne commence pas par http:// ou https:// : le ' +
            'serveur le refuse comme s’il était vide, et aucune invitation ne part.',
          ou: OU.LIVRET_INSTRUCTIONS,
        }),
      )
    }

    // ── FRAIS DE MÉNAGE (site direct) ─────────────────────────────────────
    // api/sites.js:2855-2861 — `log.pricing.cleaning_fee ?? log.cleaning_fee` ;
    // l. 2981 `if (cleaningFee == null)` ⇒ `configuration_incomplete: true`
    // avec `total_eur: null`, puis api/sites.js:3145-3149 ⇒ le devis public
    // répond `quote_incomplete`. Un `0` explicite est une valeur VALIDE (le
    // code distingue `null` de `0`, cf. son propre commentaire F-MOY-4) — donc
    // on ne signale QUE l'absence.
    // ⇒ BLOQUANT : pas de prix affichable, donc pas de réservation directe.
    const fraisMenage = log.pricing?.cleaning_fee ?? log.cleaning_fee
    if (fraisMenage == null || !Number.isFinite(Number(fraisMenage))) {
      out.push(
        manque({
          id: `log.cleaning_fee:${log.id}`,
          gravite: GRAVITE.BLOQUANT,
          portee: PORTEE.LOGEMENT,
          cible,
          champ: 'logs[].pricing.cleaning_fee',
          libelle: 'Frais de ménage facturés au voyageur',
          bloque:
            'Le site direct ne peut afficher aucun prix pour ce logement : le devis ' +
            'répond « devis incomplet ». Un montant de 0 € est une valeur valide — ' +
            'c’est l’absence de valeur qui bloque.',
          ou: OU.DIRECT,
        }),
      )
    }

    // ── CAPACITÉ MAXIMALE ─────────────────────────────────────────────────
    // api/sites.js:4555-4568 — la garde nombre de voyageurs est explicitement
    // FAIL-OPEN : « capacité IRRÉSOLUBLE (null) → on ne bloque PAS ». Même
    // chose au devis (l. 2865 : `if (maxPersons != null && ...)`).
    // ⇒ SILENCIEUX : le contrôle du nombre de voyageurs est désarmé — 12
    // personnes peuvent réserver un studio sans qu'aucune erreur n'apparaisse.
    const capacite = Number.isFinite(Number(log.capacity?.max_persons))
      ? Number(log.capacity.max_persons)
      : Number.isFinite(Number(log.capacite))
        ? Number(log.capacite)
        : null
    if (capacite == null) {
      out.push(
        manque({
          id: `log.capacity:${log.id}`,
          gravite: GRAVITE.SILENCIEUX,
          portee: PORTEE.LOGEMENT,
          cible,
          champ: 'logs[].capacity.max_persons',
          libelle: 'Nombre de voyageurs maximum',
          bloque:
            'Le contrôle du nombre de voyageurs est désactivé pour ce logement : ' +
            'une demande pour 12 personnes dans un studio passe sans erreur.',
          ou: OU.LIVRET_CARACTERISTIQUES,
        }),
      )
    }
  }
  return out
}

// ═══════════════════════════════════════════════════════════════════════════
// API PUBLIQUE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Analyse la complétude d'une configuration FBZ.
 *
 * @param {object|null} cfg — blob fbz8-cfg (`{ co, props[] }`).
 * @returns {{
 *   manques: Array<object>,
 *   compteurs: { bloquant:number, silencieux:number, informatif:number, total:number },
 *   parLogement: Map<string, Array<object>>
 * }}
 *   `manques` est trié : gravité décroissante, puis portée, puis libellé — la
 *   liste se lit de haut en bas par urgence réelle.
 */
export function analyserCompletude(cfg) {
  const manques = [...reglesSociete(cfg), ...reglesProprietaire(cfg), ...reglesLogement(cfg)]

  manques.sort((a, b) => {
    const g = ORDRE_GRAVITE[a.gravite] - ORDRE_GRAVITE[b.gravite]
    if (g !== 0) return g
    const cibleA = a.cible?.logNom || a.cible?.propNom || a.cible?.label || ''
    const cibleB = b.cible?.logNom || b.cible?.propNom || b.cible?.label || ''
    return cibleA.localeCompare(cibleB, 'fr') || a.libelle.localeCompare(b.libelle, 'fr')
  })

  const compteurs = {
    bloquant: manques.filter((m) => m.gravite === GRAVITE.BLOQUANT).length,
    silencieux: manques.filter((m) => m.gravite === GRAVITE.SILENCIEUX).length,
    informatif: manques.filter((m) => m.gravite === GRAVITE.INFORMATIF).length,
    total: manques.length,
  }

  return { manques, compteurs }
}

/**
 * Regroupe une liste de manques par cible lisible (société / propriétaire /
 * logement). Pratique pour un rendu « une carte par endroit à réparer ».
 *
 * @returns {Array<{cle:string, titre:string, sousTitre:string|null, portee:string, manques:Array<object>}>}
 *   Groupes dans l'ordre d'apparition de `manques` (donc déjà triés par gravité).
 */
export function grouperParCible(manques) {
  const groupes = []
  const index = new Map()
  for (const m of manques || []) {
    const cle =
      m.portee === PORTEE.LOGEMENT
        ? `log:${m.cible?.logId}`
        : m.portee === PORTEE.PROPRIETAIRE
          ? `prop:${m.cible?.propId}`
          : 'societe'
    if (!index.has(cle)) {
      const groupe = {
        cle,
        portee: m.portee,
        titre:
          m.portee === PORTEE.LOGEMENT
            ? m.cible?.logNom
            : m.portee === PORTEE.PROPRIETAIRE
              ? m.cible?.propNom
              : m.cible?.label || 'Société',
        sousTitre: m.portee === PORTEE.LOGEMENT ? m.cible?.propNom || null : null,
        manques: [],
      }
      index.set(cle, groupe)
      groupes.push(groupe)
    }
    index.get(cle).manques.push(m)
  }
  return groupes
}
